import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useDesignLibrary } from "@editor/hooks/useDesignLibrary";
import { useInfiniteFileTemplates } from "@editor/hooks/useInfiniteFileTemplates";
import { useInfinitePages } from "@editor/hooks/useInfinitePages";

import LoadingHeader from "@/features/react-editor/components/LoadingHeader";
import PageSelectionDropdown from "@/features/react-editor/components/navbar/PageSelectionDropdown";
import ResponsiveControls from "@/features/react-editor/components/navbar/ResponsiveControls";
import PageRenderer from "@/features/react-editor/components/PageRenderer";
import { useViewportStore } from "@/features/react-editor/store/viewportStore";
import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import PageHeader from "@replo/design-system/components/PageHeader";
import Popover from "@replo/design-system/components/popover/Popover";
import { ChevronDown } from "lucide-react";
import { useBlocker } from "react-router-dom";

import { DesignLibraryColorPresets } from "./DesignLibraryColorPresets";
import { DesignLibraryColors } from "./DesignLibraryColors";
import { DesignLibraryCustomFonts } from "./DesignLibraryCustomFonts";
import { DesignLibraryFontPresets } from "./DesignLibraryFontPresets";
import { DesignLibrarySummary } from "./DesignLibrarySummary";
import { DesignLibraryTypography } from "./DesignLibraryTypography";
import { DiscardDesignLibraryChangesModal } from "./DiscardDesignLibraryChangesModal";

export const DesignLibrary: React.FC = () => {
  const projectId = useCurrentProjectId();
  const [isPageSelectionPopoverOpen, setIsPageSelectionPopoverOpen] =
    React.useState(false);

  const {
    currentView,
    setCurrentView,
    currentStyleLibrary,
    hasUnsavedChanges,
    handleStyleLibraryChange,
    handleDiscard,
    handleSave,
    handleCustomFontsClick,
    handleCustomFontsBack,
  } = useDesignLibrary(projectId);

  const { pages } = useInfinitePages({
    projectId: projectId ?? null,
    limit: 12,
  });

  const { templates } = useInfiniteFileTemplates({
    projectId: projectId ?? null,
    type: "page",
    limit: 1,
    enabled: true,
    onlyGlobalTemplates: true,
  });

  const [selectedPageId, setSelectedPageId] = React.useState<string | null>(
    null,
  );

  const activePageId = selectedPageId ?? pages?.[0]?.id ?? null;
  const selectedPage = pages?.find((page) => page.id === activePageId) ?? null;

  const firstTemplate = templates?.[0];
  const pageRoute =
    selectedPage?.pageRoute ??
    (firstTemplate ? `/template-preview/${firstTemplate.id}` : "");

  const styleLibraryFontCSS = React.useMemo(() => {
    if (!currentStyleLibrary?.typography) {
      return "";
    }

    return Object.values(currentStyleLibrary.typography)
      .filter((font) => font.name && font.url)
      .map(
        (font) => `
        @font-face {
          font-family: '${font.name}';
          src: url('${font.url}') format('woff2'), url('${font.url}') format('woff');
          font-display: swap;
        }
      `,
      )
      .join("");
  }, [currentStyleLibrary?.typography]);

  const selectedPageName = selectedPage?.name ?? "Landing Page Template";
  const { currentViewport } = useViewportStore();

  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  const handleConfirmModal = async () => {
    await handleDiscard();
    navigationBlocker.proceed?.();
  };

  const handleCancelModal = () => {
    navigationBlocker.reset?.();
  };

  if (!projectId) {
    return null;
  }

  return (
    <div className="flex flex-col w-full h-screen overflow-hidden">
      {/* Add font-face styles for StyleLibrary fonts */}
      {styleLibraryFontCSS && (
        <style dangerouslySetInnerHTML={{ __html: styleLibraryFontCSS }} />
      )}

      <PageHeader
        title="Design Library"
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="base"
              onClick={() => void handleDiscard()}
              disabled={!hasUnsavedChanges}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              size="base"
              onClick={() => {
                void (async () => {
                  await handleSave();
                  successToast(
                    "Your style library has successfully been updated",
                  );
                })();
              }}
              disabled={!hasUnsavedChanges}
            >
              Save Changes
            </Button>
          </div>
        }
      >
        <div className="text-base text-muted -mt-4">
          Customize your global styles to keep designs on-brand.
        </div>
      </PageHeader>

      <div className="flex-1 min-h-0 overflow-hidden pb-6 pt-6">
        <div className="flex flex-col lg:flex-row items-stretch gap-6 h-full px-3 sm:px-0">
          <div className="flex flex-col items-start gap-6 flex-[1_0_0] self-stretch min-h-0">
            {currentView === "summary" && (
              <DesignLibrarySummary
                styleLibrary={currentStyleLibrary}
                onColorsClick={() => setCurrentView("colors")}
                onTypographyClick={() => setCurrentView("typography")}
                onCustomFontsClick={handleCustomFontsClick}
                onStyleLibraryChange={(styleLibrary) =>
                  void handleStyleLibraryChange(styleLibrary)
                }
              />
            )}
            {currentView === "colors" && (
              <DesignLibraryColors
                styleLibrary={currentStyleLibrary}
                onBack={() => setCurrentView("summary")}
                onPresetsClick={() => setCurrentView("presets")}
                onStyleLibraryChange={(styleLibrary) =>
                  void handleStyleLibraryChange(styleLibrary)
                }
              />
            )}
            {currentView === "presets" && (
              <DesignLibraryColorPresets
                styleLibrary={currentStyleLibrary}
                onBack={() => setCurrentView("colors")}
                onStyleLibraryChange={(styleLibrary) =>
                  void handleStyleLibraryChange(styleLibrary)
                }
              />
            )}
            {currentView === "typography" && (
              <DesignLibraryTypography
                styleLibrary={currentStyleLibrary}
                onBack={() => setCurrentView("summary")}
                onCustomFontsClick={handleCustomFontsClick}
                onFontPresetsClick={() => setCurrentView("fontPresets")}
                onStyleLibraryChange={(styleLibrary) =>
                  void handleStyleLibraryChange(styleLibrary)
                }
              />
            )}
            {currentView === "fontPresets" && (
              <DesignLibraryFontPresets
                styleLibrary={currentStyleLibrary}
                onBack={() => setCurrentView("typography")}
                onStyleLibraryChange={(styleLibrary) =>
                  void handleStyleLibraryChange(styleLibrary)
                }
              />
            )}
            {currentView === "customFonts" && (
              <DesignLibraryCustomFonts
                onBack={handleCustomFontsBack}
                onFontDeleted={handleDiscard}
              />
            )}
          </div>
          <div className="flex flex-col items-start gap-4 w-full lg:w-[642px] p-4 rounded-xl border border-border bg-white shadow-button-selectable">
            <div className="flex justify-between items-start self-stretch">
              <Popover
                isOpen={isPageSelectionPopoverOpen}
                onOpenChange={setIsPageSelectionPopoverOpen}
                hideCloseButton
                align="start"
                side="bottom"
                triggerAsChild
                content={
                  <PageSelectionDropdown
                    projectId={projectId}
                    currentPageId={selectedPageId ?? undefined}
                    handlePageSelectionCardClick={(page) => {
                      setSelectedPageId(page.id);
                      setIsPageSelectionPopoverOpen(false);
                    }}
                    hideNewPageButton
                  />
                }
                shouldPreventDefaultOnInteractOutside={false}
                className="max-h-[50vh] w-[90vw] sm:w-[60vw] lg:w-[35vw] overflow-y-auto"
              >
                <div className="flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors hover:bg-selectable-hover">
                  <span className="font-semibold line-clamp-1">
                    {selectedPageName}
                  </span>
                  <ChevronDown size={12} />
                </div>
              </Popover>
              <ResponsiveControls />
            </div>

            <div className="flex-1 w-full overflow-hidden">
              <div
                className={
                  currentViewport === "mobile"
                    ? "w-full max-w-[350px] mx-auto h-full relative"
                    : "w-full h-full relative"
                }
              >
                <LoadingHeader key={activePageId} />
                {pageRoute && (
                  <PageRenderer
                    pageRoute={pageRoute}
                    className="h-full w-full rounded-lg"
                    isInteractive={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DiscardDesignLibraryChangesModal
        isOpen={navigationBlocker.state === "blocked"}
        onOpenChange={() => {
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.reset?.();
          }
        }}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}
      />
    </div>
  );
};
